import {
  maxClipSpanSec,
  MUSIC_VIDEO_MIN_CLIP_SEC,
  normaliseMusicVideoClips,
  type MusicVideoClip,
  type MusicVideoClipSource,
  type MusicVideoDoc,
} from 'fym-shared/src/music24/musicVideoDoc';

/**
 * The edits of the clips of a cut (design music-video b1, c4, c5). Every function is pure: it takes
 * a document and gives a new one. The clips stay sorted, with no gap and no overlap, from
 * `settings.startSec` to `settings.endSec` (`normaliseMusicVideoClips`, the rule of the server).
 * A function that needs a new clip id takes `makeId`, so a test can give fixed ids.
 */

export type MakeClipId = () => string;

/** The length of the source of a clip in seconds, or null when it is not known (yet). */
export type ClipLengthOf = (source: MusicVideoClipSource) => number | null;

/**
 * What the edits need to keep a clip inside its source: the source lengths, and new ids for the
 * repeats of a loop. With no limits an edit does not check the source (an unknown length, a test).
 */
export interface ClipLimits {
  lengthOf: ClipLengthOf;
  makeId: MakeClipId;
}

/**
 * A source shorter than this does not loop: the part would be a flicker. The same value as
 * `MIN_LOOP_SOURCE_SEC` of the server plan builder (`music24-api/src/kit/plan/planFromMusicVideoDoc.ts`).
 */
export const MIN_LOOP_SOURCE_SEC = 0.5;
const EPSILON = 0.0005;
const floor3 = (value: number): number => Math.floor(value * 1000 + 1e-6) / 1000;

/** The source length that limits a clip, or null for no limit (unknown, or too short to loop). */
function limitingLength(limits: Pick<ClipLimits, 'lengthOf'> | undefined, source: MusicVideoClipSource): number | null {
  const length = limits?.lengthOf(source) ?? null;
  return length === null || !Number.isFinite(length) || length < MIN_LOOP_SOURCE_SEC ? null : length;
}

/**
 * The longest song time the clip can fill from its in point (`maxClipSpanSec` of fym-shared), or
 * null when the source does not limit it. Pure.
 */
export function clipSpanLimitSec(
  clip: Pick<MusicVideoClip, 'source' | 'inSec' | 'speed'>,
  limits: Pick<ClipLimits, 'lengthOf'> | undefined
): number | null {
  const length = limitingLength(limits, clip.source);
  return length === null ? null : maxClipSpanSec(length, clip.inSec, clip.speed);
}

/**
 * A clip that is longer than the rest of its source becomes consecutive clips of the same source:
 * the first plays from its in point to the end of the source, each next one starts the source again
 * at in point 0 (a loop) and starts on a cut. The server plan builder splits a clip the same way
 * (`clipLoopParts`), so the preview shows what the made video shows. The first part keeps the id.
 * Pure.
 */
export function loopClip(clip: MusicVideoClip, limits: ClipLimits | undefined): MusicVideoClip[] {
  const length = limitingLength(limits, clip.source);
  if (limits === undefined || length === null || !(clip.speed > 0)) {
    return [clip];
  }
  const parts: MusicVideoClip[] = [];
  let at = clip.startSec;
  // The first part keeps the in point while at least one millisecond of the source is left.
  let from = maxClipSpanSec(length, clip.inSec, clip.speed) >= 0.001 ? clip.inSec : 0;
  while (clip.endSec - at > EPSILON) {
    const end = Math.min(clip.endSec, floor3(at + maxClipSpanSec(length, from, clip.speed)));
    if (end - at <= EPSILON) {
      from = 0;
    } else {
      parts.push(
        parts.length === 0
          ? { ...clip, startSec: at, endSec: end, inSec: from }
          : { ...clip, id: limits.makeId(), startSec: at, endSec: end, inSec: from, cutIn: 'cut' }
      );
      at = end;
      from = 0;
    }
  }
  return parts.length === 0 ? [clip] : parts;
}

/** Every clip of `clipId` split into its loop (`loopClip`). */
function withLoop(clips: readonly MusicVideoClip[], clipId: string, limits: ClipLimits | undefined): MusicVideoClip[] {
  return clips.flatMap((clip) => (clip.id === clipId ? loopClip(clip, limits) : [clip]));
}

/** The part of the song that the clips cover. */
export function docRange(doc: MusicVideoDoc): { startSec: number; endSec: number } {
  return { startSec: doc.settings.startSec, endSec: doc.settings.endSec };
}

/** A new clip with the default look. Own footage is kept on recut by default (design c3). */
export function newClip(
  id: string,
  source: MusicVideoClipSource,
  startSec: number,
  endSec: number,
  inSec = 0
): MusicVideoClip {
  return {
    id,
    startSec,
    endSec,
    source,
    inSec: Math.max(0, inSec),
    speed: 1,
    move: 'still',
    cutIn: 'cut',
    fit: 'fill',
    cropX: 0.5,
    cropY: 0.5,
    keep: source.kind === 'footage',
  };
}

function withClips(doc: MusicVideoDoc, clips: MusicVideoClip[]): MusicVideoDoc {
  return { ...doc, clips: normaliseMusicVideoClips(clips, docRange(doc)) };
}

/**
 * Lays `clip` over the clips: a clip under its start ends there, a clip under its end starts there
 * (its `inSec` moves on, so the picture stays where it was), and a clip inside it goes. A clip that
 * holds the whole new clip is cut in two; the second part gets an id from `makeId`.
 */
function overwrite(clips: readonly MusicVideoClip[], clip: MusicVideoClip, makeId: MakeClipId): MusicVideoClip[] {
  const out: MusicVideoClip[] = [];
  for (const old of clips) {
    if (old.endSec <= clip.startSec || old.startSec >= clip.endSec) {
      out.push(old);
    } else {
      const hasHead = old.startSec < clip.startSec;
      if (hasHead) {
        out.push({ ...old, endSec: clip.startSec });
      }
      if (old.endSec > clip.endSec) {
        out.push({
          ...old,
          id: hasHead ? makeId() : old.id,
          startSec: clip.endSec,
          inSec: old.inSec + (clip.endSec - old.startSec) * old.speed,
        });
      }
    }
  }
  out.push(clip);
  return out.sort((a, b) => a.startSec - b.startSec);
}

interface InsertClipInput {
  id: string;
  source: MusicVideoClipSource;
  /** The place on the song (a beat under the pointer). */
  atSec: number;
  /** The length on the song. The clip ends at the end of the video at the latest. */
  lengthSec: number;
  inSec?: number;
}

/**
 * Drops a clip on the timeline at `atSec` (design c5): it takes `lengthSec` of the song from there
 * and covers what was there. Nothing changes when the place is outside the video or too short. A
 * place longer than the source keeps its length and repeats the clip (`loopClip`).
 */
export function insertClip(
  doc: MusicVideoDoc,
  input: InsertClipInput,
  makeId: MakeClipId,
  limits?: ClipLimits
): MusicVideoDoc {
  const range = docRange(doc);
  const startSec = Math.max(range.startSec, Math.min(input.atSec, range.endSec));
  const endSec = Math.min(range.endSec, startSec + input.lengthSec);
  if (endSec - startSec < MUSIC_VIDEO_MIN_CLIP_SEC) {
    return doc;
  }
  const clip = newClip(input.id, input.source, startSec, endSec, input.inSec ?? 0);
  if (doc.clips.length === 0) {
    // An empty cut: the one clip covers the whole video, like the director's plain cut.
    return withClips(doc, loopClip({ ...clip, startSec: range.startSec, endSec: range.endSec }, limits));
  }
  return withClips(doc, withLoop(overwrite(doc.clips, clip, makeId), clip.id, limits));
}

/**
 * Drops a clip on the frame (design c4): the selected clip gets the new footage and keeps its place
 * and its length. The part of the clip, the crop and the keep mark start again. A place longer than
 * the new source repeats the clip (`loopClip`).
 */
export function replaceClip(
  doc: MusicVideoDoc,
  clipId: string,
  source: MusicVideoClipSource,
  limits?: ClipLimits
): MusicVideoDoc {
  const clips = doc.clips.map((clip) =>
    clip.id === clipId
      ? {
          ...clip,
          source,
          inSec: 0,
          cropX: 0.5,
          cropY: 0.5,
          keep: clip.keep || source.kind === 'footage',
        }
      : clip
  );
  return limits === undefined ? { ...doc, clips } : withClips(doc, withLoop(clips, clipId, limits));
}

/** The fields of a clip that the inspector changes. */
export type ClipPatch = Partial<
  Pick<MusicVideoClip, 'inSec' | 'speed' | 'move' | 'cutIn' | 'fit' | 'cropX' | 'cropY' | 'keep'>
>;

/**
 * Changes the fields of a clip. With `limits`, a new in point (the trim of the inspector) stops
 * where the rest of the source just fills the clip, and a new speed that runs the clip past the end
 * of its source repeats the clip (`loopClip`).
 */
export function updateClip(doc: MusicVideoDoc, clipId: string, patch: ClipPatch, limits?: ClipLimits): MusicVideoDoc {
  const clips = doc.clips.map((clip) => {
    if (clip.id !== clipId) {
      return clip;
    }
    const next = { ...clip, ...patch, inSec: Math.max(0, patch.inSec ?? clip.inSec) };
    const length = limitingLength(limits, clip.source);
    if (patch.inSec !== undefined && length !== null) {
      const latestIn = Math.max(0, length - (next.endSec - next.startSec) * next.speed);
      next.inSec = Math.min(next.inSec, Math.max(latestIn, Math.min(clip.inSec, next.inSec)));
    }
    return next;
  });
  return patch.speed !== undefined && limits !== undefined
    ? withClips(doc, withLoop(clips, clipId, limits))
    : { ...doc, clips };
}

/**
 * Moves the cut between the clip at `index` and the next clip to `toSec` (design c5: drag a clip
 * edge; the neighbour gets shorter or longer, so the video never has a gap). Both clips keep at
 * least `MUSIC_VIDEO_MIN_CLIP_SEC`. The next clip starts later or earlier in its footage, so its
 * picture does not jump.
 */
export function moveCut(
  doc: MusicVideoDoc,
  index: number,
  toSec: number,
  limits?: Pick<ClipLimits, 'lengthOf'>
): MusicVideoDoc {
  const left = doc.clips[index];
  const right = doc.clips[index + 1];
  if (left === undefined || right === undefined) {
    return doc;
  }
  let min = left.startSec + MUSIC_VIDEO_MIN_CLIP_SEC;
  let max = right.endSec - MUSIC_VIDEO_MIN_CLIP_SEC;
  // The trim stops at the end of the source (`maxClipSpanSec`): the left clip grows no further than
  // its source gives from its in point; the right clip, whose in point stops at 0, no further than
  // its whole source. A clip that is already longer keeps its place but does not grow.
  const leftLimit = clipSpanLimitSec(left, limits);
  if (leftLimit !== null) {
    max = Math.min(max, Math.max(left.endSec, left.startSec + leftLimit));
  }
  const rightLimit = clipSpanLimitSec({ ...right, inSec: 0 }, limits);
  if (rightLimit !== null) {
    min = Math.max(min, Math.min(right.startSec, right.endSec - rightLimit));
  }
  if (min > max) {
    return doc;
  }
  const at = Math.min(max, Math.max(min, toSec));
  const shift = at - right.startSec;
  const clips = doc.clips.map((clip, position) => {
    if (position === index) {
      return { ...clip, endSec: at };
    }
    if (position === index + 1) {
      return { ...clip, startSec: at, inSec: Math.max(0, clip.inSec + shift * clip.speed) };
    }
    return clip;
  });
  return withClips(doc, clips);
}

/**
 * Moves one edge of a clip (design c5). The start of the first clip and the end of the last clip
 * are the edges of the video: they do not move here (the video settings change them).
 */
export function moveClipEdge(
  doc: MusicVideoDoc,
  clipId: string,
  edge: 'start' | 'end',
  toSec: number,
  limits?: Pick<ClipLimits, 'lengthOf'>
): MusicVideoDoc {
  const index = doc.clips.findIndex((clip) => clip.id === clipId);
  if (index < 0) {
    return doc;
  }
  return moveCut(doc, edge === 'end' ? index : index - 1, toSec, limits);
}

/** Cuts a clip in two at `atSec` (a beat). Each part keeps at least `MUSIC_VIDEO_MIN_CLIP_SEC`. */
export function splitClipAt(doc: MusicVideoDoc, clipId: string, atSec: number, newId: string): MusicVideoDoc {
  const index = doc.clips.findIndex((clip) => clip.id === clipId);
  const clip = doc.clips[index];
  if (
    clip === undefined ||
    atSec - clip.startSec < MUSIC_VIDEO_MIN_CLIP_SEC ||
    clip.endSec - atSec < MUSIC_VIDEO_MIN_CLIP_SEC
  ) {
    return doc;
  }
  const head = { ...clip, endSec: atSec };
  const tail = { ...clip, id: newId, startSec: atSec, inSec: clip.inSec + (atSec - clip.startSec) * clip.speed };
  return withClips(doc, [...doc.clips.slice(0, index), head, tail, ...doc.clips.slice(index + 1)]);
}

/**
 * Removes a clip (design b1: "Remove (the clip before fills it)"). The first clip is filled by the
 * clip after it. The only clip of a cut stays: a video always has footage.
 */
export function removeClip(doc: MusicVideoDoc, clipId: string): MusicVideoDoc {
  const index = doc.clips.findIndex((clip) => clip.id === clipId);
  const clip = doc.clips[index];
  if (clip === undefined || doc.clips.length < 2) {
    return doc;
  }
  const clips = doc.clips.filter((item) => item.id !== clipId);
  if (index === 0) {
    const [first, ...rest] = clips;
    return first === undefined ? doc : withClips(doc, [{ ...first, startSec: clip.startSec }, ...rest]);
  }
  return withClips(
    doc,
    clips.map((item, position) => (position === index - 1 ? { ...item, endSec: clip.endSec } : item))
  );
}

/**
 * Moves a whole clip so that it starts at `toSec` (design c5: drag a clip to move it). Its old place
 * fills as a remove fills it (the clip before grows; at the start the clip after). Then the clip
 * lies over the clips at the new place with the same length, footage part and look; the end of the
 * video cuts it. Nothing changes for the only clip of a cut or when the place stays the same.
 */
export function moveClip(doc: MusicVideoDoc, clipId: string, toSec: number, makeId: MakeClipId): MusicVideoDoc {
  const clip = doc.clips.find((item) => item.id === clipId);
  if (clip === undefined || doc.clips.length < 2) {
    return doc;
  }
  const range = docRange(doc);
  const startSec = Math.max(range.startSec, Math.min(toSec, range.endSec - MUSIC_VIDEO_MIN_CLIP_SEC));
  if (Math.abs(startSec - clip.startSec) < EPSILON) {
    return doc;
  }
  const endSec = Math.min(range.endSec, startSec + (clip.endSec - clip.startSec));
  const without = removeClip(doc, clipId);
  return withClips(without, overwrite(without.clips, { ...clip, startSec, endSec }, makeId));
}

/** The index of the clip of `clipId` in the cut (1-based, "Clip 13"), or null. */
export function clipNumber(doc: MusicVideoDoc, clipId: string): number | null {
  const index = doc.clips.findIndex((clip) => clip.id === clipId);
  return index < 0 ? null : index + 1;
}

/** Lays a clip over the clips (the copy of a section, the kept clips after it). */
export function overwriteClip(doc: MusicVideoDoc, clip: MusicVideoClip, makeId: MakeClipId): MusicVideoDoc {
  return withClips(doc, overwrite(doc.clips, clip, makeId));
}
